import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { Route, Routes, BrowserRouter as Router, useLocation } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider, createTheme } from "@mui/material/styles";
import * as Sentry from "@sentry/browser";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorBoundary } from "react-error-boundary";
import { CssBaseline } from "@mui/material";
import { Auth0Provider } from "@auth0/auth0-react";

import "./index.scss";
import colors from "./_colors.scss";
import "react-table-6/react-table.css";
import Header from "./components/Header.js";
import Footer from "./components/Footer.js";
import Protected from "./components/Protected.js";
import GuestOnly from "./components/GuestOnly.js";
import ErrorFallback from "./components/ErrorFallback.js";
import Snackbar from "./components/Snackbar.js";
import NotFound from "./screens/NotFound.js";
import SignIn from "./screens/SignIn.js";
import Auth from "./screens/Auth.js";
import Home from "./screens/Home.js";
import Users from "./screens/Users.js";
import Profile from "./screens/Profile.js";
import Team from "./screens/Team.js";
import Teams from "./screens/Teams.js";
import Tasks from "./screens/Tasks.js";
import TeamTasks from "./screens/TeamTasks.js";
import Task from "./screens/Task.js";
import Leaderboard from "./screens/Leaderboard.js";
import Submission from "./screens/Submission.js";
import Submissions from "./screens/Submissions.js";
import { adjustColors, jwt, colorSuggestions } from "./utils/index.js";

const {
	NODE_ENV,
	REACT_APP_AUTH0_DOMAIN,
	REACT_APP_AUTH0_CLIENT_ID,
	REACT_APP_SENTRY_DSN,
	REACT_APP_SENTRY_ENVIRONMENT,
} = process.env;

Sentry.init({
	dsn: REACT_APP_SENTRY_DSN,
	environment: REACT_APP_SENTRY_ENVIRONMENT,
	ignoreErrors: [
		"ResizeObserver loop limit exceeded",
		"Non-Error promise rejection captured",
	],
	enabled: NODE_ENV === "production",
});

const theme = createTheme({
	palette: {
		primary: {
			main: colors.primary,
			light: adjustColors(colors.primary, 100),
			dark: adjustColors(colors.primary, -80),
		},
		secondary: {
			main: colors.secondary || colorSuggestions.secondary,
			light: adjustColors(colors.secondary || colorSuggestions.secondary, 100),
			dark: adjustColors(colors.secondary || colorSuggestions.secondary, -80),
		},
		third: {
			main: colors.third || colorSuggestions.third,
			light: adjustColors(colors.third || colorSuggestions.third, 100),
			dark: adjustColors(colors.third || colorSuggestions.third, -80),
		},

		success: { main: colors.success },
		error: { main: colors.error },
		warning: { main: colors.warning },
		info: { main: colors.info },

		dark: { main: colors.dark },
		light: { main: colors.light },
		grey: { main: colors.grey, light: colors.greyLight, dark: colors.greyDark },
		green: { main: colors.green },
		white: { main: "#ffffff" },
	},
	typography: {
		fontFamily: "Commissioner, serif",
	},
});

const App = () => {
	const location = useLocation();
	const [authenticated, setAuthenticated] = useState(false);

	useEffect(() => {
		setAuthenticated(jwt.isAuthenticated());
	}, [location]);

	return (
		<StyledEngineProvider injectFirst>
			<CssBaseline />
			<ThemeProvider theme={theme}>
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Header isAuthenticated={authenticated} />
						<main style={{ position: "relative", zIndex: 0, height: `calc(100vh - ${authenticated ? "160" : "70"}px)` }}>
							<Routes>
								<Route index element={<GuestOnly c={<SignIn />} />} />
								<Route path="auth" element={<GuestOnly c={<Auth />} />} />
								<Route path="home" element={<Protected c={<Home />} />} />
								<Route path="users" element={<Protected admin c={<Users />} />} />
								<Route path="profile" element={<Protected c={<Profile />} />} />
								<Route path="team" element={<Protected c={<Team />} />} />
								<Route path="teams" element={<Protected admin c={<Teams />} />} />
								<Route path="tasks" element={<Protected admin c={<Tasks />} />} />
								<Route path="tasks/:TEAM_ID" element={<Protected admin c={<TeamTasks />} />} />
								<Route path="task" element={<Protected c={<Task />} />} />
								<Route path="leaderboard" element={<Protected c={<Leaderboard />} />} />
								<Route path="leaderboard/:TASK_ID" element={<Protected c={<Leaderboard />} />} />
								<Route path="submission" element={<Protected c={<Submission />} />} />
								<Route path="submissions" element={<Protected admin c={<Submissions />} />} />
								<Route path="*" element={<NotFound />} />
							</Routes>
						</main>
						{authenticated && <Footer /> }
						<Snackbar />
					</LocalizationProvider>
				</ErrorBoundary>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(
	<Auth0Provider
		domain={REACT_APP_AUTH0_DOMAIN}
		clientId={REACT_APP_AUTH0_CLIENT_ID}
		authorizationParams={{
			redirect_uri: window.location.origin,
		}}
	>
		<Router>
			<App />
		</Router>
	</Auth0Provider>,
);
